import {
  addGameObject,
  anchoredPos,
  changeRoomForObject,
  getEntityState,
  getZeroEntity,
  getZeroStaticEntity,
  setEntityPosition,
  setParent,
} from './entity';
import { mouseDownThisFrame, setVisibilityState, setParentByEntityProps } from './simulate';
import { TEXT_STYLE } from './constants';
import { cloneTextStyle, isAppearing, isDisappearing, v2, v2AddX } from './helpers';
import {
  EventClientToServer,
  JSON_RemoveObject,
  JSON_UpdateUserText,
  ObjectType,
  ServerEvent_EntityProps,
  ServerEvent_UserText,
} from './shared/api_shared_types';
import {
  Entity,
  VisibilityState,
  EntitySyncType,
  GameManager,
  GameObjectController,
  MoveMode,
  RoomId,
  Shape,
  SharedObjectType,
  TextObjectStyle,
  UserText,
  V2,
} from './types';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import { startUserTextEdit, stopUserTextEdit } from './user_text_editor';
import { buttonStates } from './input';
import logger from './controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: getZeroUserText,
    sync,
    update,
  };
}

export function getZeroUserText(ent: Entity): UserText {
  const newUserText: UserText = {
    ...ent,
    objectType: SharedObjectType.USER_TEXT,
    text: '',
    textObject: null,
    editor: null,
    startEditingThisFrame: false,
    stopEditingThisFrame: false,
    msg: '',
    msgText: null,
    isLocked: false,
    requestRemoveByClient: false,
    requestSyncToServer: false,
    requestStartEditMode: false,
    isDropAreaInputMode: false,
  };

  return newUserText;
}

function initUserText(
  manager: GameManager,
  userText: UserText,
  text: string,
  isCursorText = false,
  style: TextObjectStyle = TEXT_STYLE.USER_TEXT,
  minWidthMod = 3,
) {
  userText.shape = Shape.RECT;
  userText.moveMode = MoveMode.STATIC;
  userText.pushBack = true;
  userText.influenceKinetic = true;
  userText.allowDrag = true;
  userText.allowHighlight = true;
  userText.clickable = true;

  {
    let tObj = createAndAddLocalTextObject(
      manager,
      getZeroStaticEntity(),
      text,
      style,
      4,
      false,
      0,
      minWidthMod,
    );

    tObj.clickable = true;
    tObj.allowDrag = true;
    tObj.allowHighlight = !isCursorText;
    tObj.passClick = true;
    tObj.passHover = true;
    tObj.passDrag = true;
    tObj.hoverWords = !userText.isLocked;
    tObj.allowEdit = true;
    tObj.visibilityState = VisibilityState.HIDDEN;

    userText.textObject = tObj;
    setParent(tObj, userText, v2());

    userText.width = tObj.width;
    userText.height = tObj.height;
  }

  {
    let msgText = createAndAddLocalTextObject(
      manager,
      getZeroStaticEntity(),
      '',
      TEXT_STYLE.USER_TEXT_MSG,
      2,
    );

    setVisibilityState(msgText, VisibilityState.HIDDEN);

    msgText.moveMode = MoveMode.ANCHORED;
    msgText.anchoredPos = anchoredPos(v2(0, 0), v2(), v2(0, 1));

    userText.msgText = msgText;
    setParent(msgText, userText, v2());
  }

  setVisibilityState(userText, VisibilityState.APPEARING);

  return userText;
}

export function createAndAddLocalUserText(
  manager: GameManager,
  ent: Entity,
  text: string,
  isCursortext = false,
  style: TextObjectStyle = TEXT_STYLE.USER_TEXT,
  minWidthMod = 3,
): UserText {
  const userText = getZeroUserText(ent);
  addGameObject(manager, userText);
  userText.text = text;
  initUserText(manager, userText, text, isCursortext, style, minWidthMod);

  return userText;
}

//what's the difference with the prvious function?
export function createAddAndPlaceUserText(manager: GameManager, text: string, pos: V2) {
  let t = createAndAddLocalUserText(manager, getZeroEntity(), text, false);

  if (manager.mainRoom.canvas) {
    manager.mainRoom.canvas.attachChild(t, manager.mainRoom.canvas);
    const width = t.width;
    const x = manager.mainRoom.canvas.layer.relativeMousePos.x;
    const y = manager.mainRoom.canvas.layer.relativeMousePos.y;
    setEntityPosition(t, x, y);
  } else {
    changeRoomForObject(t, manager.mainRoom.roomId);
    setEntityPosition(t, pos.x, pos.y);
  }
  t.droppedThisFrame = true;
  manager.cursor.droppedEntityThisFrame = t;

  //User text is always synced
  t.requestSyncToServer = true;

  setVisibilityState(t, VisibilityState.APPEARING);
}

export function sync(manager: GameManager, userText: UserText, data: ServerEvent_UserText) {
  let entityProps: ServerEvent_EntityProps = {};

  if (data.hasOwnProperty('entityProps')) {
    entityProps = data.entityProps;
  }

  let isNew = false;

  if (!userText.id) {
    isNew = true;
    if (data.hasOwnProperty('text')) {
      userText.text = data.text;
    }
    addGameObject(manager, userText);
    initUserText(manager, userText, data.text);

    userText.syncType = EntitySyncType.SIMPLE;

    const [room, parent] = setParentByEntityProps(
      manager,
      userText,
      data.sId,
      data.roomId,
      entityProps,
    );

    if (room.canvas && parent?.parent?.objectType === ObjectType.ROOM) {
      room.canvas.attachChild(userText, room.canvas);
    }
  } else {
    if (data.hasOwnProperty('text')) {
      if (data.text !== userText.text) {
        userText.text = data.text;
        changeTextObjectText(userText.textObject, userText.text);
      }
    }
  }

  if (data.hasOwnProperty('lockedMsg')) {
    setUserTextMsg(userText, data.lockedMsg);
  }
  if (data.hasOwnProperty('isLocked')) {
    setUserTextLocked(userText, data.isLocked);
  }
}

function setUserTextLocked(userText: UserText, locked: boolean) {
  if (userText.isLocked === locked) return;

  userText.isLocked = locked;

  if (userText.isLocked) {
    userText.allowDrag = false;
  } else {
    userText.allowDrag = true;
  }

  if (userText.isLocked) {
    if (userText.editor) {
      stopUserTextEdit(userText.editor);
      userText.stopEditingThisFrame = true;
    }
    if (userText.msgText && isDisappearing(userText.msgText.visibilityState)) {
      setVisibilityState(userText.msgText, VisibilityState.APPEARING);
    }

    userText.textObject.hoverWords = false;
  } else {
    if (userText.msgText && isAppearing(userText.msgText.visibilityState)) {
      setVisibilityState(userText.msgText, VisibilityState.HIDING);
    }
    userText.textObject.hoverWords = true;
  }
}

function setUserTextMsg(userText: UserText, msg: string) {
  if (userText.msg === msg) return;

  userText.msg = msg;

  changeTextObjectText(userText.msgText, msg);
  // userText.msgText.pos = getAnchoredPosition(userText.msgText);

  if (userText.isLocked) {
    if (userText.msgText) {
      setVisibilityState(userText.msgText, VisibilityState.APPEARING);
    }
  } else {
    if (userText.msgText) {
      setVisibilityState(userText.msgText, VisibilityState.HIDING);
    }
  }
}

export function emitNewUserText(manager: GameManager, userText: UserText, roomId: RoomId) {}

function update(userText: UserText): boolean {
  userText.startEditingThisFrame = false;
  userText.stopEditingThisFrame = false;

  if (userText.requestRemoveByClient) {
    userText.requestRemoveByClient = false;
    if (userText.editor) {
      stopUserTextEdit(userText.editor);
      userText.stopEditingThisFrame = true;
    }
    if (userText.serverObjId > 0) {
      syncServerRemoveUserText(userText);
    } else {
      setVisibilityState(userText, VisibilityState.DISAPPEARING);
    }
  }

  if (
    userText.width !== userText.textObject.width ||
    userText.height !== userText.textObject.height
  ) {
    userText.width = userText.textObject.width;
    userText.height = userText.textObject.height;
    // if (userText.moveMode === MoveMode.ANCHORED) {
    //   userText.pos = getAnchoredPosition(userText);
    // }
    // userText.msgText.pos = getAnchoredPosition(userText.msgText);
  }

  const state = getEntityState(userText);
  if (userText.textObject.visibilityState !== userText.visibilityState) {
    setVisibilityState(userText.textObject, userText.visibilityState);
  }
  if (state === VisibilityState.DEFAULT) {
    if (userText.clickedThisFrame) {
      userText.requestStartEditMode = true;
    }

    if (userText.requestStartEditMode) {
      if (!userText.isLocked && !userText.editor) {
        startUserTextEdit(userText);
        userText.startEditingThisFrame = true;
        userText.textObject.hoverWords = false;
      }
      userText.requestStartEditMode = false;
    }
  }

  if (userText.editor) {
    if (
      mouseDownThisFrame() &&
      !userText.editor.mouseDownOnMe &&
      !userText.editor.removeBtn?.mouseDownOnMe
    ) {
      stopUserTextEdit(userText.editor);
      userText.stopEditingThisFrame = true;
    } else if (
      buttonStates.ENTER.changedThisFrame &&
      buttonStates.ENTER.isDown &&
      !buttonStates.SHIFT.isDown
    ) {
      stopUserTextEdit(userText.editor);
      userText.stopEditingThisFrame = true;
    }
  }

  return false;
}

export function syncServerRemoveUserText(userText: UserText) {}

export function synUserTextsToServer(userTexts: UserText[]) {
  for (let i = userTexts.length - 1; i >= 0; i--) {
    const t = userTexts[i];
    const manager = t.manager;
    if (t.requestSyncToServer) {
      if (t.serverObjId > 0) {
      } else if (!t.requestRemoveByClient) {
        emitNewUserText(manager, t, t.parentRoomId);
      }
      t.requestSyncToServer = false;
    }
  }
}

export function setUserTextFocus(userText: UserText, focus: boolean) {
  const textStyle = focus ? TEXT_STYLE.USER_TEXT_CURSOR : TEXT_STYLE.USER_TEXT_CURSOR_NO_FOCUS;

  userText.textObject.textStyle = cloneTextStyle(textStyle);

  changeTextObjectText(userText.textObject, userText.textObject.text);
}
